import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { SERVICES } from "../data/services";
import {
  FIRM_PHONE_DISPLAY,
  FIRM_PHONE_E164,
  FIRM_EMAIL,
  FIRM_ADDRESS_AR,
  FIRM_ADDRESS_EN,
  buildWhatsAppLink,
  getPageWaLink,
} from "../data/firm";

export default function Footer() {
  const { lang } = useI18n();
  const isAr = lang === "ar";

  return (
    <footer className="mt-24" style={{ background: "var(--navy-deep)", color: "rgba(245,241,232,0.85)" }}>
      <div className="container-luxe py-16 lg:py-20 grid gap-12 lg:grid-cols-12">
        {/* Brand */}
        <div className="lg:col-span-4">
          <img src="/branding/logo-transparent.png" alt="Aldakheel Legal Group"
               className="hero-logo h-16 w-auto object-contain mb-6" />
          <p className={`${isAr ? "font-arabic" : "font-display"} text-lg leading-relaxed`}
             style={{ color: "var(--gold-soft)" }}>
            {isAr ? "المرافعة. النزاهة. النتائج." : "Advocacy. Integrity. Results."}
          </p>
          <p className={`mt-5 ${isAr ? "font-arabic" : ""} text-sm leading-relaxed max-w-md`}
             style={{ color: "rgba(245,241,232,0.7)" }}>
            {isAr
              ? "مكتب محاماة كويتي مرخص. خبرة 9+ سنوات، 2000+ قضية، عبر كل درجات القضاء الكويتي."
              : "Kuwait-licensed law firm. 9+ years, 2,000+ matters, across every Kuwaiti court."}
          </p>
          <a href={getPageWaLink("consultation", lang)} target="_blank" rel="noopener noreferrer" data-testid="footer-book-link"
                className="inline-flex items-center mt-8 px-6 h-11 btn-gold rounded-sm text-sm font-medium">
            {isAr ? "احجز استشارة عبر واتساب" : "Book via WhatsApp"}
          </a>
        </div>

        {/* Navigate */}
        <div className="lg:col-span-2">
          <p className="eyebrow mb-4" style={{ color: "var(--gold-soft)" }}>
            {isAr ? "تصفح" : "Navigate"}
          </p>
          <ul className="space-y-3 text-sm">
            {[
              ["/", isAr ? "الرئيسية" : "Home"],
              ["/about", isAr ? "من نحن" : "About"],
              ["/services", isAr ? "خدماتنا" : "Services"],
              ["/ai-legal-assistant", isAr ? "الذكاء القانوني" : "AI Assistant"],
              ["/team", isAr ? "الفريق" : "Team"],
              ["/contact", isAr ? "تواصل" : "Contact"],
            ].map(([to, label]) => (
              <li key={to}><Link to={to} className="hover:text-white">{label}</Link></li>
            ))}
          </ul>
        </div>

        {/* Services */}
        <div className="lg:col-span-3">
          <p className="eyebrow mb-4" style={{ color: "var(--gold-soft)" }}>
            {isAr ? "خدماتنا" : "Services"}
          </p>
          <ul className="grid grid-cols-1 gap-2.5 text-sm">
            {SERVICES.slice(0, 6).map((s) => (
              <li key={s.slug}>
                <Link to={`/services/${s.slug}`} className="hover:text-white">
                  {isAr ? s.titleAr : s.titleEn}
                </Link>
              </li>
            ))}
            <li><Link to="/services" className="hover:text-white" style={{ color: "var(--gold-soft)" }}>
              {isAr ? "كل الخدمات ←" : "All services →"}
            </Link></li>
          </ul>
        </div>

        {/* Contact */}
        <div className="lg:col-span-3">
          <p className="eyebrow mb-4" style={{ color: "var(--gold-soft)" }}>
            {isAr ? "تواصل" : "Contact"}
          </p>
          <ul className="space-y-4 text-sm">
            <li className="flex items-start gap-3">
              <Phone className="w-4 h-4 mt-1 shrink-0" style={{ color: "var(--gold-soft)" }} />
              <a href={`tel:${FIRM_PHONE_E164}`} dir="ltr" className="hover:text-white">{FIRM_PHONE_DISPLAY}</a>
            </li>
            <li className="flex items-start gap-3">
              <MessageCircle className="w-4 h-4 mt-1 shrink-0" style={{ color: "var(--gold-soft)" }} />
              <a href={buildWhatsAppLink()} target="_blank" rel="noopener noreferrer" className="hover:text-white">
                {isAr ? "محادثة واتساب" : "WhatsApp chat"}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Mail className="w-4 h-4 mt-1 shrink-0" style={{ color: "var(--gold-soft)" }} />
              <a href={`mailto:${FIRM_EMAIL}`} dir="ltr" className="hover:text-white">{FIRM_EMAIL}</a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin className="w-4 h-4 mt-1 shrink-0" style={{ color: "var(--gold-soft)" }} />
              <span className={isAr ? "font-arabic" : ""}>
                {isAr ? FIRM_ADDRESS_AR : FIRM_ADDRESS_EN}
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div style={{ borderTop: "1px solid rgba(201,169,97,0.15)" }}>
        <div className="container-luxe py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs"
             style={{ color: "rgba(245,241,232,0.55)" }}>
          <p>{isAr
            ? "© 2026 مجموعة الدخيل القانونية. جميع الحقوق محفوظة."
            : "© 2026 Aldakheel Legal Group. All rights reserved."}</p>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Link to="/subscriptions" data-testid="footer-subscriptions-link" className="hover:text-white">
              {isAr ? "الاشتراكات" : "Subscriptions"}
            </Link>
            <Link to="/refund-policy" data-testid="footer-refund-link" className="hover:text-white">
              {isAr ? "سياسة الاسترداد" : "Refunds"}
            </Link>
            <Link to="/ai-disclaimer" data-testid="footer-ai-disclaimer-link" className="hover:text-white">
              {isAr ? "إخلاء المسؤولية" : "AI disclaimer"}
            </Link>
            <Link to="/confidentiality-policy" data-testid="footer-confidentiality-link" className="hover:text-white">
              {isAr ? "السرية" : "Confidentiality"}
            </Link>
            <Link to="/payment-policy" data-testid="footer-payment-link" className="hover:text-white">
              {isAr ? "الدفع" : "Payment"}
            </Link>
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-white">
              {isAr ? "الخصوصية" : "Privacy"}
            </Link>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-white">
              {isAr ? "الشروط" : "Terms"}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
